import React, { createContext, useContext, useState } from 'react';
import type { AccessAnalysisReport, CoverageAnalysisReport } from '../types/mission';
import type { DrawerMode, LayerState, MDCockpitState, ModelData, ReportHoverHighlights, SelectedItemType, ViewMode } from '../types/mdai';

const DEFAULT_LAYERS: LayerState = {
  satellites:   true,
  trails:       true,
  ground:       true,
  geofeatures:  true,
  groundTracks: false,
  sensorFovs:   false,
  gsFov:        false,
  los:          false,
  losSatGS:     false,
  labels:       false,
};

const EMPTY_REPORT_HOVER_HIGHLIGHTS: ReportHoverHighlights = {
  satelliteIds: [],
  groupIds: [],
  geoFeatureIds: [],
  groundStationIds: [],
};

const DEFAULT_STATE: MDCockpitState = {
  aiPanelOpen:        false,
  drawerMode:         'closed',
  selectedItemId:     null,
  selectedItemType:   null,
  selectedGeoFeatureIds: [],
  orbitalEditTargetId: null,
  groupEditTargetId:  null,
  layers:             DEFAULT_LAYERS,
  addSatModalOpen:    false,
  addGSModalOpen:     false,
  editingGSId:        null,
  geoFeaturePanelOpen: false,
  editingFeatureId:   null,
  analyserOpen:       false,
  coverageAnalyserOpen: false,
  sensorConfigOpen:   false,
  modelConfigOpen:    false,
  viewMode:           '3D',
  modelData:          null,
  leftPanelCollapsed: false,
  timelineCollapsed:  false,
  timelineHeight:     168,
  reportHoverHighlights: EMPTY_REPORT_HOVER_HIGHLIGHTS,
  shouldBlockUI:      false,
  analyserSourceIds:  [],
  analyserGfIds:      [],
  analyserGsIds:      [],
  selectedReportId:   null,
  selectedReportDetail: null,
  selectedCoverageReportId: null,
  selectedCoverageReportDetail: null,
  selectedCoverageSensors: {},
  allFilters:         { advanceFilters: [] },
  summaryData:        null,
  eventsData:         null,
  coverageSectionTab: 'simple',
  heatmapMetricMode:  'max',
  revisitThresholds:  [2, 4, 6, 8],
  accessWindowStatus: null,
  timelineResetTrigger: 0,
};

interface UIContextValue {
  state: MDCockpitState;
  toggleAIPanel: () => void;
  setDrawerMode: (mode: DrawerMode) => void;
  selectItem: (id: string | null) => void;
  selectTreeItem: (id: string | null, type: SelectedItemType | null) => void;
  toggleSelectedGeoFeature: (id: string) => void;
  openSatelliteOrbitEdit: (id: string) => void;
  openGroupOrbitEdit: (id: string) => void;
  toggleLayer: (key: string) => void;
  openAddSat: () => void;
  closeAddSat: () => void;
  openAddGS: (id?: string) => void;
  closeAddGS: () => void;
  openGeoFeature: () => void;
  closeGeoFeature: () => void;
  openEditGeoFeature: (id: string) => void;
  openAnalyser: () => void;
  closeAnalyser: () => void;
  openCoverageAnalyser: () => void;
  closeCoverageAnalyser: () => void;
  openSensorConfig: () => void;
  closeSensorConfig: () => void;
  setLeftPanelCollapsed: (v: boolean) => void;
  openModelConfig: () => void;
  closeModelConfig: () => void;
  setViewMode: (mode: ViewMode) => void;
  setModelData: (data: ModelData | null) => void;
  setTimelineCollapsed: (collapsed: boolean) => void;
  setTimelineHeight: (height: number) => void;
  setReportHoverHighlights: (highlights: ReportHoverHighlights | null) => void;
  setShouldBlockUI: (block: boolean) => void;
  toggleAnalyserSource: (id: string) => void;
  toggleAnalyserGf: (id: string) => void;
  toggleAnalyserGs: (id: string) => void;
  clearAnalyserSelection: () => void;
  setAnalyserSelection: (sources: string[], gfs: string[], gss: string[]) => void;
  setSelectedReportId: (id: string | null) => void;
  setSelectedReportDetail: (report: AccessAnalysisReport | null) => void;
  setSelectedCoverageReportId: (id: string | null) => void;
  setSelectedCoverageReportDetail: (report: CoverageAnalysisReport | null) => void;
  setSelectedCoverageSensors: (sensors: Record<string, string> | ((prev: Record<string, string>) => Record<string, string>)) => void;
  setAllFilters: (filters: any) => void;
  setSummaryData: (data: any) => void;
  setEventsData: (data: any) => void;
  setCoverageSectionTab: (tab: 'simple' | 'gap' | 'revisit') => void;
  setHeatmapMetricMode: (mode: 'max' | 'avg' | 'min' | 'count' | 'duration' | 'std') => void;
  setRevisitThresholds: (thresholds: number[]) => void;
  setAccessWindowStatus: (status: 'ready' | 'computing' | 'failed' | null) => void;
  triggerTimelineReset: () => void;
}

const UIContext = createContext<UIContextValue | null>(null);

export const UIProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<MDCockpitState>(DEFAULT_STATE);

  const value: UIContextValue = {
    state,
    triggerTimelineReset: () => setState((s) => ({ ...s, timelineResetTrigger: (s.timelineResetTrigger ?? 0) + 1 })),
    toggleAIPanel:   () => setState((s) => ({
      ...s,
      aiPanelOpen: !s.aiPanelOpen,
      ...(s.aiPanelOpen
        ? { selectedItemId: null, selectedItemType: null, selectedGeoFeatureIds: [], orbitalEditTargetId: null, groupEditTargetId: null }
        : {}),
    })),
    setDrawerMode:   (mode) => setState((s) => ({ ...s, drawerMode: mode })),
    selectItem:      (id) => setState((s) => ({ ...s, selectedItemId: id, selectedItemType: id ? s.selectedItemType : null, selectedGeoFeatureIds: id && s.selectedItemType === 'geofeature' ? s.selectedGeoFeatureIds : [], orbitalEditTargetId: null, groupEditTargetId: null })),
    selectTreeItem:  (id, type) => setState((s) => ({
      ...s,
      selectedItemId: id,
      selectedItemType: type,
      selectedGeoFeatureIds: type === 'geofeature' && id ? [id] : [],
      orbitalEditTargetId: null,
      groupEditTargetId: null,
      geoFeaturePanelOpen: false,
      editingFeatureId: null,
      // Close creation modals when selecting an item from the tree
      ...(id ? {
        addSatModalOpen: false,
        addGSModalOpen: false,
        modelConfigOpen: false,
      } : {}),
    })),
    toggleSelectedGeoFeature: (id) => setState((s) => {
      const isRemoving = s.selectedGeoFeatureIds.includes(id);
      const nextIds = isRemoving
        ? s.selectedGeoFeatureIds.filter((selectedId) => selectedId !== id)
        : [...s.selectedGeoFeatureIds, id];

      return {
        ...s,
        selectedItemId: nextIds.length > 0 ? (isRemoving ? nextIds[nextIds.length - 1] : id) : null,
        selectedItemType: nextIds.length > 0 ? 'geofeature' : null,
        selectedGeoFeatureIds: nextIds,
        orbitalEditTargetId: null,
        groupEditTargetId: null,
        geoFeaturePanelOpen: false,
        editingFeatureId: null,
        ...(nextIds.length > 0 ? {
          addSatModalOpen: false,
          addGSModalOpen: false,
          modelConfigOpen: false,
        } : {}),
      };
    }),
    openSatelliteOrbitEdit: (id) => setState((s) => ({
      ...s,
      selectedItemId: null,
      selectedItemType: null,
      selectedGeoFeatureIds: [],
      orbitalEditTargetId: id,
      geoFeaturePanelOpen: false,
      editingFeatureId: null,
      addSatModalOpen: true,
      addGSModalOpen: false,
      modelConfigOpen: false,
      analyserOpen: false,
    })),
    openGroupOrbitEdit: (id) => setState((s) => ({
      ...s,
      selectedItemId: null,
      selectedItemType: null,
      selectedGeoFeatureIds: [],
      orbitalEditTargetId: null,
      groupEditTargetId: id,
      geoFeaturePanelOpen: false,
      editingFeatureId: null,
      addSatModalOpen: true,
      addGSModalOpen: false,
      modelConfigOpen: false,
      analyserOpen: false,
    })),
    toggleLayer:     (key) => setState((s) => {
      const nextValue = !s.layers[key as keyof LayerState];
      const nextLayers = { ...s.layers, [key]: nextValue };
      if (key === 'los') {
        nextLayers.losSatGS = nextValue;
      }
      return {
        ...s,
        layers: nextLayers,
      };
    }),
    openAddSat:      () => setState((s) => ({
      ...s,
      addSatModalOpen: true,
      addGSModalOpen: false,
      geoFeaturePanelOpen: false,
      editingFeatureId: null,
      modelConfigOpen: false,
      selectedItemId: null,
      selectedItemType: null,
      selectedGeoFeatureIds: [],
      orbitalEditTargetId: null,
      groupEditTargetId: null,
      analyserOpen: false,
    })),
    closeAddSat:     () => setState((s) => ({ ...s, addSatModalOpen: false, groupEditTargetId: null, orbitalEditTargetId: null })),
    openAddGS:       (id) => {
      // Ensure editingGSId is either a string or null, never an object
      const normalizedId = typeof id === 'string' ? id : null;
      console.log('[UIContext] openAddGS called with:', id, '→ normalized to:', normalizedId);
      return setState((s) => ({
        ...s,
        addGSModalOpen: true,
        addSatModalOpen: false,
        geoFeaturePanelOpen: false,
        editingFeatureId: null,
        modelConfigOpen: false,
        selectedItemId: null,
        selectedItemType: null,
        selectedGeoFeatureIds: [],
        orbitalEditTargetId: null,
        editingGSId: normalizedId,
        analyserOpen: false,
      }));
    },
    closeAddGS:      () => setState((s) => ({ ...s, addGSModalOpen: false, editingGSId: null })),
    openGeoFeature:  () => setState((s) => ({
      ...s,
      geoFeaturePanelOpen: true,
      editingFeatureId: null,
      addSatModalOpen: false,
      addGSModalOpen: false,
      modelConfigOpen: false,
      selectedItemId: null,
      selectedItemType: null,
      selectedGeoFeatureIds: [],
      orbitalEditTargetId: null,
      analyserOpen: false,
    })),
    closeGeoFeature: () => setState((s) => ({
      ...s,
      geoFeaturePanelOpen: false,
      editingFeatureId: null,
      ...(s.selectedItemType === 'geofeature'
        ? { selectedItemId: null, selectedItemType: null, selectedGeoFeatureIds: [], orbitalEditTargetId: null }
        : {}),
    })),
    openEditGeoFeature: (id) => setState((s) => ({
      ...s,
      geoFeaturePanelOpen: true,
      editingFeatureId: id,
      addSatModalOpen: false,
      addGSModalOpen: false,
      modelConfigOpen: false,
      analyserOpen: false,
    })),
    openAnalyser:    () => setState((s) => ({
      ...s,
      analyserOpen: true,
      coverageAnalyserOpen: false,
      addSatModalOpen: false,
      addGSModalOpen: false,
      geoFeaturePanelOpen: false,
      editingFeatureId: null,
      modelConfigOpen: false,
      selectedItemId: null,
      selectedItemType: null,
      orbitalEditTargetId: null,
      groupEditTargetId: null,
      selectedGeoFeatureIds: [],
    })),
    closeAnalyser:   () => setState((s) => ({
      ...s,
      analyserOpen: false,
      addSatModalOpen: false,
      addGSModalOpen: false,
      geoFeaturePanelOpen: false,
      editingFeatureId: null,
      modelConfigOpen: false,
      selectedItemId: null,
      selectedItemType: null,
      orbitalEditTargetId: null,
      groupEditTargetId: null,
    })),
    openCoverageAnalyser: () => setState((s) => ({
      ...s,
      coverageAnalyserOpen: true,
      analyserOpen: false,
      addSatModalOpen: false,
      addGSModalOpen: false,
      geoFeaturePanelOpen: false,
      editingFeatureId: null,
      modelConfigOpen: false,
      selectedItemId: null,
      selectedItemType: null,
      orbitalEditTargetId: null,
      groupEditTargetId: null,
      selectedGeoFeatureIds: [],
    })),
    closeCoverageAnalyser: () => setState((s) => ({
      ...s,
      coverageAnalyserOpen: false,
      sensorConfigOpen: false,
      addSatModalOpen: false,
      addGSModalOpen: false,
      geoFeaturePanelOpen: false,
      editingFeatureId: null,
      modelConfigOpen: false,
      selectedItemId: null,
      selectedItemType: null,
      orbitalEditTargetId: null,
      groupEditTargetId: null,
    })),
    openSensorConfig: () => setState((s) => ({
      ...s,
      sensorConfigOpen: true,
    })),
    closeSensorConfig: () => setState((s) => ({
      ...s,
      sensorConfigOpen: false,
    })),
    setLeftPanelCollapsed: (v) => setState((s) => ({ ...s, leftPanelCollapsed: v })),
    openModelConfig: () => setState((s) => ({
      ...s,
      modelConfigOpen: true,
      addSatModalOpen: false,
      addGSModalOpen: false,
      geoFeaturePanelOpen: false,
      editingFeatureId: null,
      selectedItemId: null,
      selectedItemType: null,
      selectedGeoFeatureIds: [],
      orbitalEditTargetId: null,
      analyserOpen: false,
      coverageAnalyserOpen: false,
    })),
    closeModelConfig: () => setState((s) => ({ ...s, modelConfigOpen: false })),
    setViewMode:      (mode) => setState((s) => ({ ...s, viewMode: mode })),
    setModelData:     (data) => setState((s) => ({ ...s, modelData: data })),
    setTimelineCollapsed: (collapsed) => setState((s) => ({ ...s, timelineCollapsed: collapsed })),
    setTimelineHeight: (height) => setState((s) => ({ ...s, timelineHeight: Math.max(96, Math.min(340, height)) })),
    setReportHoverHighlights: (highlights) => setState((s) => ({
      ...s,
      reportHoverHighlights: highlights ?? EMPTY_REPORT_HOVER_HIGHLIGHTS,
    })),
    setShouldBlockUI: (block) => setState((s) => ({
      ...s,
      shouldBlockUI: block,
    })),
    toggleAnalyserSource: (id) => setState((s) => ({
      ...s,
      analyserSourceIds: s.analyserSourceIds.includes(id)
        ? s.analyserSourceIds.filter((x) => x !== id)
        : [...s.analyserSourceIds, id],
    })),
    toggleAnalyserGf: (id) => setState((s) => ({
      ...s,
      analyserGfIds: s.analyserGfIds.includes(id)
        ? s.analyserGfIds.filter((x) => x !== id)
        : [...s.analyserGfIds, id],
    })),
    toggleAnalyserGs: (id) => setState((s) => ({
      ...s,
      analyserGsIds: s.analyserGsIds.includes(id)
        ? s.analyserGsIds.filter((x) => x !== id)
        : [...s.analyserGsIds, id],
    })),
    clearAnalyserSelection: () => setState((s) => ({
      ...s,
      analyserSourceIds: [],
      analyserGfIds: [],
      analyserGsIds: [],
    })),
    setAnalyserSelection: (sources, gfs, gss) => setState((s) => ({
      ...s,
      analyserSourceIds: sources,
      analyserGfIds: gfs,
      analyserGsIds: gss,
    })),
    setSelectedReportId: (id) => setState((s) => ({ ...s, selectedReportId: id })),
    setSelectedReportDetail: (report) => setState((s) => ({ ...s, selectedReportDetail: report })),
    setSelectedCoverageReportId: (id) => setState((s) => ({ ...s, selectedCoverageReportId: id })),
    setSelectedCoverageReportDetail: (report) => setState((s) => ({ ...s, selectedCoverageReportDetail: report })),
    setSelectedCoverageSensors: (sensors) => setState((s) => ({
      ...s,
      selectedCoverageSensors: typeof sensors === 'function' ? sensors(s.selectedCoverageSensors) : sensors,
    })),
    setAllFilters: (filters) => setState((s) => ({
      ...s,
      allFilters: typeof filters === 'function' ? filters(s.allFilters) : filters
    })),
    setSummaryData: (data) => setState((s) => ({
      ...s,
      summaryData: typeof data === 'function' ? data(s.summaryData) : data
    })),
    setEventsData: (data) => setState((s) => ({
      ...s,
      eventsData: typeof data === 'function' ? data(s.eventsData) : data
    })),
    setCoverageSectionTab: (tab) => setState((s) => ({ ...s, coverageSectionTab: tab })),
    setHeatmapMetricMode: (mode) => setState((s) => ({ ...s, heatmapMetricMode: mode })),
    setRevisitThresholds: (thresholds) => setState((s) => ({ ...s, revisitThresholds: thresholds })),
    setAccessWindowStatus: (status) => setState((s) => ({ ...s, accessWindowStatus: status })),
  };

  return <UIContext.Provider value={value}>{children}</UIContext.Provider>;
};

export function useUI(): UIContextValue {
  const ctx = useContext(UIContext);
  if (!ctx) throw new Error('useUI must be used inside UIProvider');
  return ctx;
}
